import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useContractTypes, useOrganization } from '../../hooks/useQueries'
import { useLocale } from '../../contexts/LocaleContext'
import { useToast } from '../../hooks/use-toast'
import { apiErrorDetail } from '../../lib/apiError'
import { AssetPicker } from '../AssetPicker'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { contractTypeLabel, CurrencySelect, useRefreshListings } from './listingShared'

/**
 * Create a draft: the object, the contract type (lease family) and the
 * currency. The currency proposed from the jurisdiction is offered, never
 * applied silently.
 */
export function ListingCreateDialog({ org, open, onOpenChange, assetUid, assetName }: { org: string; open: boolean; onOpenChange: (o: boolean) => void; assetUid?: string; assetName?: string }) {
    const { t } = useTranslation('listings')
    const { locale } = useLocale()
    const { toast } = useToast()
    const navigate = useNavigate()
    const refresh = useRefreshListings()
    const [asset, setAsset] = useState<{ uid: string; name: string } | undefined>(assetUid ? { uid: assetUid, name: assetName ?? '' } : undefined)
    const [contractType, setContractType] = useState('LEASE')
    // The organization's default currency until the person picks another one.
    const { data: organization } = useOrganization(org)
    const [pickedCurrency, setCurrency] = useState('')
    const currency = pickedCurrency || organization?.currency || ''

    const { data: types = [] } = useContractTypes(org, { enabled: open })
    const leaseTypes = types.filter((ct) => ct.code?.startsWith('LEASE'))

    const { data: suggestion } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/listings/currency-suggestion',
        { params: { path: { orgCode: org }, query: { asset_uid: asset?.uid ?? '' } } },
        { enabled: open && !!asset },
    )
    const suggested = suggestion?.currency

    const create = $api.useMutation('post', '/api/v1/organizations/{orgCode}/listings', {
        onSuccess: (res) => {
            refresh()
            onOpenChange(false)
            navigate({ to: '/$org/listings/$listingUid', params: { org, listingUid: res.listing.uid } })
        },
        onError: (err) => toast({ title: t('create.error'), description: apiErrorDetail(err, t('create.error')), variant: 'destructive' }),
    })

    const submit = () => {
        if (!asset || !currency) return
        create.mutate({
            params: { path: { orgCode: org } },
            body: { asset_uid: asset.uid, contract_type_code: contractType, currency },
        })
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="listing-create-dialog">
                <DialogHeader>
                    <DialogTitle>{t('create.title')}</DialogTitle>
                    <DialogDescription>{t('create.description')}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="mb-1 block text-sm">{t('create.asset')}</Label>
                        <AssetPicker org={org} value={asset?.uid} valueLabel={asset?.name} testId="listing-create-asset" onChange={(uid, name) => setAsset({ uid, name })} />
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm">{t('create.contract_type')}</Label>
                        <Select value={contractType} onValueChange={setContractType}>
                            <SelectTrigger data-testid="listing-contract-type-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {(leaseTypes.length ? leaseTypes : [{ code: 'LEASE' }]).map((ct) => (
                                    <SelectItem key={ct.code} value={ct.code} data-testid={`listing-contract-type-${ct.code}`}>
                                        {contractTypeLabel(ct, locale)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="mb-1 block text-sm">{t('create.currency')}</Label>
                        <div className="flex items-center gap-2">
                            <div className="w-32">
                                <CurrencySelect value={currency} onChange={setCurrency} testId="listing-currency-select" />
                            </div>
                            {suggested && suggested !== currency && (
                                <Button type="button" variant="outline" size="sm" onClick={() => setCurrency(suggested)} data-testid="listing-currency-accept-suggestion">
                                    {t('create.accept_currency', { currency: suggested })}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="listing-create-cancel">
                        {t('cancel')}
                    </Button>
                    <Button onClick={submit} disabled={!asset || !currency || create.isPending} data-testid="listing-create-submit">
                        {t('create.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
